/* eslint-disable camelcase */
import React from 'react';
import { ContentFragment } from '@oneaudi/falcon-tools';
import { listOfDealersType } from '../redux/appConfigReducer';
import { TypedPrice } from '../redux/priceBreakdownReducer';

export interface DealershipLocation {
  address: {
    city: string;
    latitude: number;
    longitude: number;
    region: string;
    street: string;
    zipCode: string;
  };
  distance?: number;
  kvpsid: string;
  kvpsSyncId: string;
  name: string;
}

export interface PreSetDealershipLocation {
  kvpsid?: string;
  kvpsSyncId: string;
  name: string;
}

export type DealershipList = (DealershipLocation | PreSetDealershipLocation)[];

export interface Coordinates {
  latitude: number;
  longitude: number;
}

export interface AutocompleteFeature {
  center: {
    latitude: number;
    longitude: number;
  };
  placeName: string;
  placeType: 'postcode' | 'district' | 'place' | 'locality' | 'neighborhood' | 'address' | 'poi';
  id: string;
  text: string;
  address: string;
  properties: {
    address: string;
  };
  context: {
    [key: string]: {
      id: string;
      text: string;
      shortCode?: string;
    };
  };
}

export type LocationType = {
  lat?: number;
  lng?: number;
};

export type PlaceResultType = {
  formattedAddress?: string;
  name?: string;
  placeId?: string;
  url?: string;
  vicinity?: string;
  types?: Array<string>;
  city?: string;
  region?: string;
  postalCode?: string;
  geometry: {
    location: LocationType;
  };
};

export interface SelectedUserLocation {
  userCoordinates: Coordinates;
  // feature: AutocompleteFeature;    **OLD TYPE (MapBox) // FIXME Later
  feature: PlaceResultType;
}

export type LocationSearchDataByRadius = {
  mode: string;
  radius: number;
  dealershipList: DealershipList;
  selectedUserLocation: SelectedUserLocation;
  isPreSetPartner: false;
  isPartnerHeader?: boolean;
};

export type LocationSearchDataByDealers = {
  mode: string;
  dealershipList: DealershipList;
  // if we get pre set dealership in a query param the user will not select a location
  selectedUserLocation?: SelectedUserLocation;
  isPreSetPartner: boolean;
  isPartnerHeader?: boolean;
};

export type LocationSearchData = LocationSearchDataByRadius | LocationSearchDataByDealers;

export type FilterOption = {
  label: string | React.ReactNode;
  ariaLabel: string;
  value: string;
  popoverDescription?: string;
  image?: React.FC<{ color?: string }> | string;
  selectedImage?: React.FC<{ color?: string }>;
  disabled?: boolean;
  tabs?: string[];
  hint?: string;
  isFilterTagNotRemovable?: boolean;
  isHidden?: boolean;
};

export type PartnerDealerOption = {
  dealerName: string;
  dealerId: string;
  isMainDealer: boolean;
};

export type ModelYearOption = {
  [key: number]: {
    label: string;
    ariaLabel: string;
    value: number;
    isFilterTagNotRemovable: boolean;
  };
};

export type RangeFilterOption = {
  label: string | React.ReactNode;
  ariaLabel: string;
  value: number;
  popoverDescription?: string;
  image?: React.FC<{ color?: string }> | string;
  selectedImage?: React.FC<{ color?: string }>;
  disabled?: boolean;
  tabs?: string[];
  hint?: string;
};

export type RangeOptions = {
  min: RangeFilterOption;
  max: RangeFilterOption;
};

export type BodyTypeImages = {
  [key: string]: {
    type: string;
    __typename: string;
    url: React.FC<any>;
  }[];
};

export type TabbedFilterOptions = {
  tabTitle: string;
  options: FilterOption[];
}[];

export type GalleryImagesType = {
  type: string | null;
  url: string;
}[];

export interface NonAudiDealershipLocation {
  multiLocation: boolean;
  dealerId: string;
  dealerName: string;
}

export interface Rebate {
  Name: string;
  Value: number;
  IdentCode: string;
  offerDescription: string;
  offerEndDate: string;
  offerStartDate: string;
}

export const NEGATIVE_PRICE_KEYS = ['dealerDiscount', 'OEMDiscount', 'nonConditionalBonus'];

export const PricesPriorityOrder = [
  'regular',
  'OEMDiscount',
  'dealerMarkup',
  'dealerDocFees',
  'destinationAndHandlingFees',
  'dealerDiscount',
  'dealerPrice',
  'nonConditionalBonus',
  'retail',
  'final',
];

export const UsedPricesPriorityOrder = [
  'regular',
  'retail',
  'dealerPrice',
  'dealerDiscount',
  'dealerMarkup',
  'OEMDiscount',
  'dealerDocFees',
  'destinationAndHandlingFees',
  'nonConditionalBonus',
  'final',
];

export interface FilteredVehicle {
  id: string;
  interiorColor: string | null;
  exteriorColor: string | null;
  genericExtColor: string | null;
  modelID: string;
  modelMake: string;
  modelYear: string;
  modelCode: string;
  modelName: string;
  modelPrice: string;
  modelPriceRetail: string;
  modelPowerkW: string | null;
  modelMileage: string | null;
  typedPrices: TypedPrice[];
  audiCode: string;
  stockNumber: string;
  trimName: string;
  trimlineCode: string | null;
  kvpsSyncId: string;
  dealerName: string;
  dealerRegion: string;
  vehicleType: string;
  warrantyType: string | null;
  modelImageFromScs: string;
  isAvailableNow: boolean | null;
  vin: string;
  bodyType: string | null;
  saleOrderType: string | null;
  vehicleInventoryType: string;
  vehicleOrderStatus: string | null;
  driveType: string | null;
  gearType: string | null;
  fuelType: string | null;
  distanceFromUser: number;
  galleryImages: GalleryImagesType;
  vehicleDealerImages: string[] | null;
  nonAudiDealershipLocation: NonAudiDealershipLocation;
  rebate: Rebate[] | null;
}

type ActiveCount = {
  active: boolean;
  count: number;
};

type AvailableInMonthsResutCounts = {
  selected: number;
  minValue: number;
};

type HasWarrantiesFilterResultCount = {
  all: ActiveCount;
  no: ActiveCount;
  yes: ActiveCount;
};

export type BodyTypeFilterResultCounts = {
  allroad_quattro: ActiveCount;
  avant: ActiveCount;
  cabriolet: ActiveCount;
  coupe: ActiveCount;
  limousine: ActiveCount;
  roadster: ActiveCount;
  sedan: ActiveCount;
  sportback: ActiveCount;
  spyder: ActiveCount;
  suv: ActiveCount;
};

type ModelFamilyFilterResultCounts = {
  a3: ActiveCount;
  a4: ActiveCount;
  a5: ActiveCount;
  a6: ActiveCount;
  a7: ActiveCount;
  a8: ActiveCount;
  etron: ActiveCount;
  etrongt: ActiveCount;
  etrons: ActiveCount;
  q3: ActiveCount;
  q4: ActiveCount;
  q5: ActiveCount;
  q5tfsie: ActiveCount;
  q7: ActiveCount;
  q8: ActiveCount;
  q8etron: ActiveCount;
  sq8etron: ActiveCount;
  r8: ActiveCount;
  rs3: ActiveCount;
  rs4: ActiveCount;
  rs5: ActiveCount;
  rs6: ActiveCount;
  rs7: ActiveCount;
  rsetrongt: ActiveCount;
  rsq8: ActiveCount;
  s3: ActiveCount;
  s4: ActiveCount;
  s5: ActiveCount;
  s6: ActiveCount;
  s7: ActiveCount;
  s8: ActiveCount;
  sq5: ActiveCount;
  sq7: ActiveCount;
  sq8: ActiveCount;
  tt: ActiveCount;
  ttrs: ActiveCount;
  tts: ActiveCount;
};

type SliderResultCounts = {
  min: {
    minValue: number;
    selected: number;
  };
  max: {
    maxValue: number;
    selected: number;
  };
};

type TrimLineResultCounts = {
  comfort: ActiveCount;
  progressive: ActiveCount;
  technik: ActiveCount;
  premium: ActiveCount;
  premium_plus: ActiveCount;
  prestige: ActiveCount;
  other: ActiveCount;
};

type WheelSizeResultCounts = {
  size_15: ActiveCount;
  size_16: ActiveCount;
  size_17: ActiveCount;
  size_18: ActiveCount;
  size_19: ActiveCount;
  size_20: ActiveCount;
  size_21: ActiveCount;
  size_22: ActiveCount;
  size_23: ActiveCount;
};

type GearBoxResultCounts = {
  automatic: ActiveCount;
  manual: ActiveCount;
};

type DrivetrainResultCounts = {
  quattro: ActiveCount;
  fwd: ActiveCount;
  rwd: ActiveCount;
};

type FuelTypeResultCounts = {
  gasoline: ActiveCount;
  diesel: ActiveCount;
  electrical: ActiveCount;
  gasoline_hybrid: ActiveCount;
};

type ExteriorColorResultCount = {
  black: ActiveCount;
  white: ActiveCount;
  gray: ActiveCount;
  silver: ActiveCount;
  blue: ActiveCount;
  red: ActiveCount;
  green: ActiveCount;
  brown: ActiveCount;
  yellow: ActiveCount;
  beige: ActiveCount;
  orange: ActiveCount;
  default: ActiveCount;
};

type InteriorColorResultCount = {
  black: ActiveCount;
  white: ActiveCount;
  gray: ActiveCount;
  red: ActiveCount;
  brown: ActiveCount;
  beige: ActiveCount;
  default: ActiveCount;
};

type RangeSlidersCount = {
  count: number;
  max: {
    maxValue: number;
    selected: number;
  };
  min: {
    minValue: number;
    selected: number;
  };
};

export type NonAudiCountType = {
  active: boolean;
  code: string;
  count: number;
};

export interface RangeResultsCounts {
  modelYear: RangeSlidersCount;
  powerHP: RangeSlidersCount;
  priceRetail: RangeSlidersCount;
  usedCarMileage: RangeSlidersCount;
}

export type SortMethodsTypes = {
  [key: string]: {
    [key: string]: string;
  };
};

export type NonAudiFilterResultCounts = {
  bodyTypes: NonAudiCountType[] | null;
  exteriorColors: NonAudiCountType[] | null;
  fuelTypes: NonAudiCountType[] | null;
  manufactures: NonAudiCountType[] | null;
  models: NonAudiCountType[] | null;
};

export interface FilterResultCounts {
  totalCount: number | null;
  totalNewCarCount: number | null;
  totalUsedCarCount: number | null;
  available_from_soon: number | null;
  available_from_immediately: number | null;
  available_in_months: AvailableInMonthsResutCounts;
  foreign_brand_no: number | null;
  foreign_brand_yes: number | null;
  nonAudiManufactures: NonAudiCountType[] | null;
  nonAudiModels: NonAudiCountType[] | null;
  has_warranties: HasWarrantiesFilterResultCount;
  body_type: BodyTypeFilterResultCounts;
  model_range: ModelFamilyFilterResultCounts;
  modelYear: SliderResultCounts;
  prices_retail: SliderResultCounts;
  power: SliderResultCounts;
  usedCarMileage: SliderResultCounts;
  trimline: TrimLineResultCounts;
  wheel: WheelSizeResultCounts;
  drive: DrivetrainResultCounts;
  gear_type: GearBoxResultCounts;
  vtp_drivetrain: FuelTypeResultCounts;
  interior_color_seats: InteriorColorResultCount;
  exterior_color: ExteriorColorResultCount;
  nonAudiFilterResultCounts: NonAudiFilterResultCounts;
}

export type Sort =
  | 'mileage:DESC'
  | 'mileage:ASC'
  | 'salesModelYear:DESC'
  | 'salesModelYear:ASC'
  | 'prices.retail:DESC'
  | 'prices.retail:ASC'
  | 'byDistance:ASC'
  | 'byDistance:DESC'
  | '';

export type UsedInventoryType =
  | 'has-warranties.yes'
  | 'has-warranties.no'
  | 'has-warranties.all'
  | 'has-warranties.combined';

export type ModelFamily =
  | 'model-range.a3'
  | 'model-range.a4'
  | 'model-range.a5'
  | 'model-range.s5'
  | 'model-range.a6'
  | 'model-range.a7'
  | 'model-range.s7'
  | 'model-range.a8'
  | 'model-range.s8'
  | 'model-range.q3'
  | 'model-range.q4'
  | 'model-range.q5'
  | 'model-range.q5tfsie'
  | 'model-range.sq5'
  | 'model-range.q7'
  | 'model-range.sq7'
  | 'model-range.q8'
  | 'model-range.q8etron'
  | 'model-range.sq8etron'
  | 'model-range.sq8'
  | 'model-range.rs3'
  | 'model-range.rs5'
  | 'model-range.rs6'
  | 'model-range.rs7'
  | 'model-range.r8'
  | 'model-range.rsq8'
  | 'model-range.tt'
  | 'model-range.tts'
  | 'model-range.ttrs'
  | 'model-range.etron'
  | 'model-range.etrons'
  | 'model-range.etrongt'
  | 'model-range.rsetrongt';

export type GearBox = 'gear-type.automatic' | 'gear-type.manual';

export type Drivetrain = 'drive.quattro' | 'drive.fwd' | 'drive.rwd';

export type FuelType =
  | 'vtp-drivetrain.gasoline'
  | 'vtp-drivetrain.diesel'
  | 'vtp-drivetrain.electrical'
  | 'vtp-drivetrain.gasoline_hybrid';

export type TrimLine =
  | 'trimline.comfort'
  | 'trimline.progressive'
  | 'trimline.technik'
  | 'trimline.premium'
  | 'trimline.premium_plus'
  | 'trimline.prestige'
  | 'trimline.other';

export type BodyType =
  | 'body-type.suv'
  | 'body-type.limousine'
  | 'body-type.allroad_quattro'
  | 'body-type.coupe'
  | 'body-type.sportback'
  | 'body-type.spyder'
  | 'body-type.roadster'
  | 'body-type.cabriolet'
  | 'body-type.avant';

export type WheelSize =
  | 'wheel-size.15'
  | 'wheel-size.16'
  | 'wheel-size.17'
  | 'wheel-size.18'
  | 'wheel-size.19'
  | 'wheel-size.20'
  | 'wheel-size.21'
  | 'wheel-size.22'
  | 'wheel-size.23';

export type ExteriorColor =
  | 'black'
  | 'white'
  | 'gray'
  | 'silver'
  | 'blue'
  | 'red'
  | 'green'
  | 'brown'
  | 'yellow'
  | 'beige'
  | 'orange'
  | 'default';

export type InteriorColor = 'black' | 'white' | 'gray' | 'red' | 'brown' | 'beige' | 'default';

export interface Filters {
  vehicleType: 'new' | 'used';
  availableInMonths: number | null;
  usedInventoryType: UsedInventoryType;
  modelFamily: ModelFamily[];
  bodyType: BodyType[];
  price: number[];
  modelYear: number[];
  mileage: number[];
  power: number[];
  gearBox: GearBox[];
  drivetrain: Drivetrain[];
  fuelType: FuelType[];
  wheelSize: WheelSize[];
  exteriorColor: ExteriorColor[];
  trimLine: TrimLine[];
  interiorColor: InteriorColor[];
  tier3Dealers: string[];
  partnerDealers: PartnerDealerOption[];
}

export type FilterSetter = (
  name: keyof Filters,
  newValue: string | number[] | string[] | number | null,
) => void;

export type FiltersSetter = (incoming: {
  [filterName in keyof Filters]?: string | number[] | string[] | number | null;
}) => void;

export interface VehicleFilterBag {
  vehicles: FilteredVehicle[] | undefined;
  filterResults: FilterResultCounts | undefined;
  rangeResults: RangeResultsCounts | undefined;
  onLoadMore: () => void;
  loading: boolean;
  canLoadMore: boolean;
  setSortedBy: (sortedBy: Sort) => void;
  sortedBy: Sort;
  filters: Filters;
  setVehicleType: (newValue: 'new' | 'used') => void;
  setFilter: FilterSetter;
  setFilters: FiltersSetter;
  resetFilters: () => void;
  isFilteringActive: boolean;
  shouldListVehicles: boolean;
}

export interface FilteredWormwoodVehiclesQuery {
  version: string;
  market: string[];
  limit: number;
  lang?: string;
  filters: string;
  sort: Sort;
  offset: number;
  preset: string;
}
export interface FilterCardsProps {
  category: keyof Filters;
  translations: {
    [key: string]: string;
  };
  valueFormatting?: 'price' | 'mileage' | 'power';
  handleClearFilterFlag: () => void;
}

export interface FalconConfig {
  __type?: 'aem-headless';

  config: Partial<ContentFragment> & {
    fields: {
      bodyType: string[];
      driveTrain?: string;
      exteriorColor?: string;
      fuelType?: string;
      gearBox?: string;
      interiorColor?: string;
      isBodyTypeEnabled: boolean;
      isDriveTrainEnabled: boolean;
      isFuelTypeEnabled: boolean;
      isImageGalleryAble: boolean;
      isMakeTypeEnabled: boolean;
      isModelFamilyEnabled: boolean;
      isPowerEnabled: boolean;
      isTier3page: boolean;
      isVehicleTypeEnabled: boolean;
      listOfDealers: string[];
      makeTypes: string[];
      marketType?: string;
      mileageMax?: number;
      mileageMin?: number;
      modelFamily: string[];
      modelYearMax?: string;
      modelYearMin?: string;
      powerMax?: number;
      powerMin?: number;
      priceMax?: number;
      priceMin?: number;
      trimLine: string[];
      vehicleTypePreset: string;
      wheelSize?: string;
    };
  };
}
